import { Box, Container, Typography, Divider, Paper } from "@mui/material";
import { theme } from "./theme";
import useTranslationState from "./useTranslationState";

function Terms() {
    const translations = useTranslationState({
        termsTitle: 'Terms of Service',
        lastUpdated: 'Last Updated',
        lastUpdatedDate: 'May 1, 2023',
        agreement: 'Agreement',
        agreementText: 'By accessing or using our website, you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the website.',
        intellectualProperty: 'Intellectual Property',
        intellectualPropertyText: 'The Service and all contents, including but not limited to text, images, graphics or code are the property of Ukoni GmbH and are protected by copyright, trademarks, database and other intellectual property rights.',
        userAccount: 'User Account',
        userAccountText: 'When you create an account with us, you must provide information that is accurate, complete, and current at all times. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.',
        prohibitedUses: 'Prohibited Uses',
        prohibitedUsesText: 'You may not access or use the Site for any purpose other than that for which we make the Site available. The Site may not be used in connection with any commercial endeavors except those that are specifically endorsed or approved by us.',
        limitation: 'Limitation of Liability',
        limitationText: 'To the maximum extent permitted by applicable law, in no event shall Ukoni GmbH be liable for any indirect, punitive, incidental, special, consequential damages or any damages whatsoever including, without limitation, damages for loss of use, data or profits, arising out of or in any way connected with the use or performance of the website.',
        termination: 'Termination',
        terminationText: 'We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease.',
        governing: 'Governing Law',
        governingText: 'These Terms shall be governed and construed in accordance with the laws of Germany, without regard to its conflict of law provisions.',
        changes: 'Changes to Terms',
        changesText: 'We reserve the right, at our sole discretion, to modify or replace these Terms at any time. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.',
        contactUs: 'Contact Us',
        contactUsText: 'If you have any questions about these Terms, please contact us.'
    });

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: theme.palette.primary.dark,
                paddingY: 12,
                minHeight: '100vh',
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        padding: { xs: 3, sm: 5 },
                        border: '1px solid rgba(255,255,255,0.1)',
                        opacity: 0,
                        animation: 'fadeIn 0.8s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                        '@keyframes fadeIn': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(10px)',
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0)',
                            },
                        },
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            color: 'white',
                            mb: 1,
                            fontWeight: 500,
                            textAlign: 'center',
                        }}
                    >
                        {translations.termsTitle}
                    </Typography>
                    
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'rgba(255,255,255,0.7)',
                            textAlign: 'center',
                            mb: 4,
                        }}
                    >
                        {translations.lastUpdated}: {translations.lastUpdatedDate}
                    </Typography>
                    
                    <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.agreement}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.agreementText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.intellectualProperty}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.intellectualPropertyText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.userAccount}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.userAccountText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.prohibitedUses}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.prohibitedUsesText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.limitation}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.limitationText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.termination}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.terminationText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.governing}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.governingText}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.changes}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.changesText}
                        </Typography>
                    </Box>
                    
                    <Box>
                        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                            {translations.contactUs}
                        </Typography>
                        
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                lineHeight: 1.8,
                            }}
                        >
                            {translations.contactUsText}
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

export default Terms; 